import React from "react";
import { Link } from "react-router-dom";
import { Gauge, Star, ShieldAlert, CheckCircle, ArrowRight } from "lucide-react";

/* ── Outputs ─────────────────────────────────────────────────────────────── */
const OUTPUTS = [
  {
    icon: <Gauge size={22} />,
    metric: "Carbon Intensity",
    unit: "kg CO₂e per ₹1,000 spend",
    desc:
      "A normalised measure of carbon emissions relative to total spend. Allows comparison across entities of different sizes and sectors.",
  },
  {
    icon: <Star size={22} />,
    metric: "Green Score",
    unit: "0 – 100 index",
    desc:
      "A composite score reflecting overall carbon efficiency. Higher scores indicate lower carbon intensity relative to sector peers and spend profile.",
  },
  {
    icon: <ShieldAlert size={22} />,
    metric: "Risk Classification",
    unit: "3-tier system",
    desc:
      "A structured risk tier assigned based on carbon intensity and Green Score — providing an immediately actionable risk signal for lenders and analysts.",
  },
];

/* ── Risk tiers ──────────────────────────────────────────────────────────── */
const RISK_TIERS = [
  {
    tier: "Green Transition Ready",
    color: "tier-green",
    score: "Score 67–100",
    desc: "Low carbon intensity. Entity demonstrates spend patterns aligned with a low-carbon transition pathway.",
    signal: "Low Risk",
  },
  {
    tier: "Transition Required",
    color: "tier-amber",
    score: "Score 34–66",
    desc: "Moderate carbon intensity. Carbon reduction measures recommended. Eligible for transition finance instruments.",
    signal: "Moderate Risk",
  },
  {
    tier: "High Carbon Exposure",
    color: "tier-red",
    score: "Score 0–33",
    desc: "Elevated carbon intensity. Significant spend in high-emission sectors. Immediate transition planning advised.",
    signal: "Elevated Risk",
  },
];

/* ── Access model ────────────────────────────────────────────────────────── */
const ACCESS_TIERS = [
  {
    name: "Free Starter",
    tag: "No Card Required",
    tagColor: "tag-green",
    price: "₹0 / Free",
    desc: "Sign up with your email and get 2 screening runs instantly. No credit card, no approval process.",
    features: [
      "2 free screening runs",
      "Full CSV upload & parsing",
      "Carbon intensity calculation",
      "Green Score generation",
      "Risk tier classification",
      "Downloadable report",
    ],
    cta: "Get Started Free",
    to: "/login",
    primary: true,
  },
  {
    name: "MSME / Direct",
    tag: "Bank-Issued Code",
    tagColor: "tag-amber",
    price: "Access Code Required",
    desc: "Use a time-limited code issued by your bank, lender, or the platform team for extended screening.",
    features: [
      "All Free Starter features",
      "Extended run quota",
      "Organisation-level reports",
      "Green certificate issuance",
      "Valid per access code term",
      "Priority support",
    ],
    cta: "Sign In with Code",
    to: "/login",
    primary: false,
  },
  {
    name: "Institutional",
    tag: "For Banks & Partners",
    tagColor: "tag-teal",
    price: "Contact us",
    desc: "Bulk access code issuance for financial institutions screening loan portfolios or supply chains.",
    features: [
      "Volume access code generation",
      "Portfolio-level carbon reports",
      "API integration available",
      "Dedicated account management",
      "Custom methodology notes",
      "Regulatory disclosure support",
    ],
    cta: "Request a Demo",
    href: "mailto:carbon@rsustain.com",
    primary: false,
  },
];

export default function PricingSection() {
  return (
    <section className="outputs-section" id="outputs">
      <div className="landing-container">

        {/* ── Outputs ── */}
        <div className="section-label">OUTPUTS</div>
        <h2 className="section-title">What You Receive</h2>
        <p className="section-sub">
          Every screening run produces three structured outputs — designed
          for immediate use in lending decisions, ESG disclosures, and supplier assessments.
        </p>

        <div className="outputs-grid">
          {OUTPUTS.map((o, i) => (
            <div key={i} className="output-card">
              <div className="output-card-icon">{o.icon}</div>
              <div className="output-metric">{o.metric}</div>
              <div className="output-unit">{o.unit}</div>
              <p className="output-desc">{o.desc}</p>
            </div>
          ))}
        </div>

        {/* ── Risk classification ── */}
        <div className="section-label" style={{ marginTop: "5rem" }}>RISK CLASSIFICATION</div>
        <h2 className="section-title" id="risk-tiers">Understanding Your Risk Tier</h2>
        <p className="section-sub">
          Each entity is assigned one of three risk tiers based on their carbon intensity
          and Green Score. Tiers are designed to be immediately interpretable by non-technical stakeholders.
        </p>

        <div className="risk-tiers-list">
          {RISK_TIERS.map((t, i) => (
            <div key={i} className={`risk-tier-card ${t.color}`}>
              <div className="risk-tier-left">
                <div className="risk-tier-signal">{t.signal}</div>
                <div className="risk-tier-name">{t.tier}</div>
                <div className="risk-tier-score">{t.score}</div>
              </div>
              <div className="risk-tier-desc">{t.desc}</div>
            </div>
          ))}
        </div>

        {/* ── Access model ── */}
        <div className="section-label" style={{ marginTop: "5rem" }}>ACCESS MODEL</div>
        <h2 className="section-title" id="access">Platform Access</h2>
        <p className="section-sub">
          Start for free with your email — no credit card required. Scale up with a bank-issued
          code or institutional account for extended screening and portfolio-level reports.
        </p>

        <div className="access-grid">
          {ACCESS_TIERS.map((tier, i) => (
            <div key={i} className={`access-card${tier.primary ? " access-card--primary" : ""}`}>
              <div className="access-card-header">
                <span className={`access-tag ${tier.tagColor}`}>{tier.tag}</span>
                <div className="access-name">{tier.name}</div>
                <div className="access-price">{tier.price}</div>
                <p className="access-desc">{tier.desc}</p>
              </div>
              <ul className="access-features">
                {tier.features.map((f, j) => (
                  <li key={j}>
                    <CheckCircle size={14} />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="access-cta">
                {tier.to ? (
                  <Link to={tier.to} className={`access-btn${tier.primary ? " access-btn--primary" : ""}`}>
                    {tier.cta}
                    <ArrowRight size={14} />
                  </Link>
                ) : (
                  <a href={tier.href} className="access-btn">
                    {tier.cta}
                    <ArrowRight size={14} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
